/**
 * The Legend card — one renderer, used by Command's gallery and the Syndicate
 * Charter's leadership panel.
 *
 * Its own module rather than living in either layer's UI file, because both
 * would otherwise have to import the other. Pure render: no gameState reads, no
 * DOM writes, so it can be dropped into any container.
 *
 * FIELDS ARE OPTIONAL BY DESIGN. A Command Legend snapshot carries the full tour
 * record (tech gear, artifacts, T3 wins); a Syndicate Leader snapshot carries
 * only the identity and the marks that elected them. Rendering only what is
 * present is what lets one card serve both, rather than two cards drifting.
 *
 * Colour comes in as `accent`/`accentDim` — the layers have different identity
 * colours and the card is the same object in both.
 */

import * as config from '../constants.js';
import { getAvatarSeed } from './util.js';

/**
 * One stat line, or nothing when the field is absent.
 *
 * Values are coloured with an inline style, NOT `text-[${accent}]`. That was a
 * Tailwind arbitrary-value class built from a runtime variable, so the
 * build-time scanner never generated it — there is not a single `text-[#...]`
 * rule in the compiled CSS, and every Legend stat rendered unstyled. Same family
 * as the dead `text-navy`. Runtime colour has to be an inline style.
 */
function statLine(label, value, accent) {
    if (value === undefined || value === null) return '';
    return `${label}: <span class="tracking-tighter" style="color:${accent}">${value}</span><br/>`;
}

/**
 * @param {object} legend - a Legend or Leader snapshot
 * @param {object} opts
 * @param {string} opts.accent      - layer identity colour
 * @param {string} opts.accentDim   - border colour
 * @param {string} [opts.title]     - eyebrow above the name ("Syndicate Leader")
 * @param {string} [opts.note]      - muted footnote under the stats
 */
export function renderLegendCard(legend, { accent, accentDim, title = '', note = '' } = {}) {
    if (!legend) return '';
    const faction = config.factionSkills[legend.faction] || {};
    const marks = (legend.marksEarned || 0).toLocaleString();

    return `
        <div class="flex gap-3 bg-white/5 rounded-lg p-3" style="border:1px solid ${accentDim};">
            <div class="relative w-[96px] h-[96px] flex-shrink-0 rounded-lg border-2 border-gray-500 bg-gradient-to-b from-gray-800 to-gray-900 overflow-hidden">
                <div class="absolute inset-0 flex items-center justify-center">
                    <div class="contractor-avatar-placeholder" data-avatar-seed="${getAvatarSeed(legend)}" data-avatar-faction="${legend.faction}"></div>
                </div>
                <div class="absolute top-1 left-1 bg-black bg-opacity-40 border border-gray-500 rounded-full w-5 h-5 flex items-center justify-center text-xs" title="${faction.name || legend.faction}">${faction.icon || '🏢'}</div>
                ${legend.tour
                    ? `<div class="absolute bottom-0 left-0 right-0 text-center text-[10px] tracking-widest py-0.5 bg-black/75" style="color:${accent}">TOUR ${legend.tour}</div>`
                    : ''}
            </div>
            <div class="text-xs leading-relaxed min-w-0">
                ${title ? `<div class="text-[10px] uppercase tracking-widest text-gray-400">${title}</div>` : ''}
                <div class="font-bold text-white text-base">${legend.name}</div>
                ${legend.promotedRole ? `Now: <span class="font-bold" style="color:${accent}">${legend.promotedRole}</span><br/>` : ''}
                ${statLine('Max Lv.', legend.level, accent)}
                ${statLine('Skill Gear', legend.techGear === undefined ? undefined : `${legend.techGear}/15 crafted`, accent)}
                ${statLine('Artifacts Unlocked', legend.artifactsUnlocked, accent)}
                ${statLine('Artifacts Crafted', legend.artifactsCrafted, accent)}
                ${statLine('T3 Successful', legend.t3Wins, accent)}
                <span class="text-game-gold tracking-tighter">Tour Marks: ${marks}🎖️</span>
                ${note ? `<div class="text-gray-400 mt-1">${note}</div>` : ''}
            </div>
        </div>`;
}
